import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { BlockUI, NgBlockUI }                                 from 'ng-block-ui';
import { TranslateService }                                   from '@ngx-translate/core';

import { CSVService }             from '../core/services/csv.service';
import { ImportOptionsModel }     from './typings/imports.option';
import { ImportsService }         from './imports.service';
import { ImportProposedColumnModel } from './typings/imports.proposed-mapping';
import { ImportRunSummaryModel }   from './typings/imports.summary';
import { ImportStructureModel }   from './typings/imports.import-structure';
import { ImportTheirColumnModel } from './typings/imports.their-column';
import { MessagesService }        from '../messages/messages.service';

@Component({
  selector   : 'wor-import',
  styles: [ require('./import.component.scss') ],
  template: require('./import.component.html')
})
export class ImportComponent implements OnDestroy, OnInit {
  @BlockUI('importBlock') block : NgBlockUI;

  @Input() importOptions: ImportOptionsModel;

  // WS-6801. Told to whoever is showing this import, so the hub above can bring its
  // count and its ticks up to date. The hub's heading is on screen the whole time an
  // import runs, and until now it went on saying nothing had been done while the
  // import underneath reported thousands of records added.
  @Output() finished = new EventEmitter<void>();

  @ViewChild('fileInput') fileInput: ElementRef;

  csvArray               : Array<Array<string>> = [];
  errors                 : Array<number>        = [];
  fileName               : string;
  importStructure        : Array<ImportStructureModel>;
  loading                : boolean;
  ourTransformedColumns  : Array<any>;
  rowHidden              : Array<boolean>;
  selectColumnVisible    : boolean;
  selectedFile           : File;
  theirColumns           : Array<ImportTheirColumnModel>;
  theirTransformedColumns: Array<ImportTheirColumnModel>;

  hasHeaders = true;

  // WS-6784. True only while a file is held over the drop zone, so the zone can show it
  // will accept the drop before the operator lets go of it.
  isDragging = false;

  // WS-6750. The run this business already has going, picked up when the screen opens.
  // Null when there is none, which is the normal case.
  runInFlight : ImportRunSummaryModel;

  // WS-6751. Columns the proposal was not confident about, raised to the operator as
  // questions instead of being guessed at. Empty when there is no proposal, which is
  // every import until WS-6749 ships.
  proposalQuestions : Array<{ index : number, question : string }> = [];

  // True once a proposal has been applied, so the screen can say the mapping was
  // suggested rather than letting the operator assume they built it.
  proposalApplied = false;

  // True only when a proposal was asked for and could not be had. An import that never
  // asks for one is not "unavailable", it is simply manual, and saying otherwise would
  // report a problem that does not exist.
  proposalUnavailable = false;

  constructor (
    protected csvService      : CSVService,
    protected importsService  : ImportsService,
    protected messagesService : MessagesService,
    protected translateService: TranslateService
  ) {}

  addColumn ( columnName : string ) : void {
    const selected = this.theirColumns.find(column => column.name === columnName);

    this.theirTransformedColumns.push({
      name  : selected.name,
      values: [ ...selected.values ]
    });

    this.importStructure.push({
      action    : '',
      ours      : '',
      theirs    : selected.index,
      user_input: []
    });

    this.ourTransformedColumns.push(null);
    this.rowHidden.push(false);

    this.selectColumnVisible = false;
  }

  analyze () : void {
    this.block.start();

    this.loading = true;

    this.csvService.parse(this.selectedFile)
    .then(( csvArray : Array<Array<string>> ) => {
      this.csvArray = csvArray;

      this.buildGrid();

      return this.applyProposedMapping();
    })
    .catch(error => {
      this.messagesService.error(error);

      this.fileInput.nativeElement.value = '';
    })
    .finally(() => {
      this.block.stop();

      this.loading = false;
  });
  }

  // WS-6751. Opens the screen with a mapping already proposed, so the operator's job is
  // confirming rather than building. Everything proposed can be overridden, and nothing
  // is imported until they submit, so a proposal is never acted on unconfirmed.
  applyProposedMapping () : Promise<void> {
    this.proposalQuestions   = [];
    this.proposalApplied     = false;
    this.proposalUnavailable = false;

    const headings = ( this.theirColumns || [] ).map(column => column.name);

    const rows = ( this.csvArray || [] ).slice(this.hasHeaders ? 1 : 0);

    return this.importsService
    .fetchProposedMapping(this.importOptions.proposalUrl, this.importOptions.forTable,
                          headings, rows)
    .then(proposal => {
      if ( !proposal ) {
        // Only a problem if one was actually asked for.
        this.proposalUnavailable = !!this.importOptions.proposalUrl;

        return;
      }

      proposal.columns.forEach(column => this.applyProposedColumn(column));

      this.proposalApplied = true;
    });
  }

  applyProposedColumn ( column : ImportProposedColumnModel ) : void {
    const index = Number(column.theirs);

    if ( !this.importStructure || !this.importStructure[ index ] ) {
      return;
    }

    // Not confident, so this is a question rather than a proposal. Left unmapped and
    // put to the operator, because a wrong mapping applied quietly is worse than one
    // more thing to answer.
    if ( !column.confident || !column.ours ) {
      this.proposalQuestions.push({
        index   : index,
        question: column.question || ''
      });

      return;
    }

    this.moveToStructure(index, column.ours);

    // A rule the field implies is deterministic and moveToStructure has already set it.
    // A proposed rule is a suggestion, so it does not overrule one.
    const implied = this.importsService
      .identifierActionFor(this.importOptions.url, column.ours);

    if ( column.action && !implied ) {
      this.importStructure[ index ].action     = column.action;
      this.importStructure[ index ].user_input = [];
    }
  }

  // Questions first, in the order the columns appear in the file.
  // WS-6750. Still going, so the screen says so instead of looking idle.
  get isRunning () : boolean {
    return [ 'queued', 'running' ].includes(this.runInFlight?.status);
  }

  // Waiting on an answer, or done. Both are the summary's job.
  get showsSummary () : boolean {
    return [ 'awaiting_decisions', 'finished' ].includes(this.runInFlight?.status);
  }

  // WS-6807. Nothing has been written by a run that stopped to ask, so leaving it is
  // free. Dropping the run releases the summary and returns the operator to the columns.
  onSummaryBack () : void {
    this.runInFlight = null;
  }

  // WS-6807. The answers are saved and the worker is queued to pick the run up again a
  // few seconds later, rather than resumed on the spot. Until it does, the run is still
  // parked, so handing straight to watchRun would read awaiting_decisions, stop at once
  // and leave the operator looking at the questions they have just answered.
  //
  // So wait for the run to actually move, then follow it exactly as a straight-through
  // import is followed. A run that parks a second time comes back here through the
  // summary remounting, which reloads it.
  onDecisionsApplied () : void {
    // The progress display goes up first and stays up for the whole resume, including
    // the few seconds before the worker claims the run. Leaving it until the run moves
    // was the earlier mistake: the operator answered and got a blank screen telling
    // them it was safe to leave, at the exact moment the design needs them to stay.
    this.importsService.resumeProgress()
    .catch(() => this.importsService.cancelImport());

    this.awaitResume().then(() => {
      this.importsService.watchRun(current => {
        this.runInFlight = current;

        this.importsService.updateStatus(current.status);
      })
      .then(( finished : ImportRunSummaryModel ) => {
        this.importsService.closeProgress();

        this.runInFlight = finished;

        if ( finished?.status !== 'finished' ) {
          return;
        }

        this.messagesService.show(
          finished.result_message || this.translateService.instant('IMPORT_SPACE.IMPORT_FINISHED'),
          'success');

        this.finished.emit();
      });
    });
  }

  // Polls until the run leaves the parked state. Bounded rather than open ended: a
  // worker that never arrives should leave the screen showing the questions, which is
  // the truth, instead of waiting for ever on something that is not coming.
  protected awaitResume ( attempts = 0 ) : Promise<void> {
    if ( attempts >= 15 ) {
      return Promise.resolve();
    }

    return this.importsService.fetchRunSummary()
    .then(summary => {
      this.runInFlight = summary;

      if ( summary && summary.status !== 'awaiting_decisions' ) {
        return;
      }

      return new Promise<void>(resolve => setTimeout(resolve, 2000))
      .then(() => this.awaitResume(attempts + 1));
    });
  }

  // Rows in the operator's file, header excluded. The summary needs it to work out how
  // many will import from how many will not.
  get totalRows () : number {
    if ( !this.csvArray?.length ) {
      return 0;
    }

    return this.hasHeaders ? this.csvArray.length - 1 : this.csvArray.length;
  }

  get orderedQuestions () : Array<{ index : number, question : string }> {
    return [ ...this.proposalQuestions ].sort((a, b) => a.index - b.index);
  }

  isQuestion ( index : number ) : boolean {
    return this.proposalQuestions.some(question => question.index === index);
  }

  // WS-6782. The operator's own values, so a question about a column is answered by
  // looking at it rather than by guessing from its name. Blanks and repeats are
  // dropped, because four copies of the same value tell them nothing.
  exampleValues ( index : number ) : Array<string> {
    const values = this.theirTransformedColumns?.[ index ]?.values || [];

    return values
      .map(value => ( value === null || value === undefined ) ? '' : String(value).trim())
      .filter(value => !!value)
      .filter(( value, position, list ) => list.indexOf(value) === position)
      .slice(0, 4);
  }

  // WS-6781. The mapping is read rather than built.
  //
  // The old grid gave every column two empty dropdowns and the better part of two
  // hundred pixels, so a thirty six column file was a scroll rather than a glance, and
  // the two columns actually needing an answer sat buried among the thirty four that
  // did not. Each column now states what it was matched to and whether it still wants
  // the operator, and only the row they open shows the controls.
  expandedRow : number = null;

  // Columns wanting an answer first, then the rest in file order. Ordering by state
  // rather than by position is the point: two decisions in a file of thirty six should
  // not have to be hunted for.
  get columnOrder () : Array<number> {
    const indexes = ( this.theirTransformedColumns || [] ).map(( column, index ) => index);

    return indexes.sort(( a, b ) => {
      const aFirst = this.needsDecision(a) ? 0 : 1;
      const bFirst = this.needsDecision(b) ? 0 : 1;

      return aFirst === bFirst ? a - b : aFirst - bFirst;
    });
  }

  get columnCount () : number {
    return ( this.theirTransformedColumns || [] ).length;
  }

  get decisionCount () : number {
    return ( this.theirTransformedColumns || [] )
      .filter(( column, index ) => this.needsDecision(index)).length;
  }

  get matchedCount () : number {
    return ( this.theirTransformedColumns || [] )
      .filter(( column, index ) => this.isMatched(index)).length;
  }

  // Deliberately set aside by the operator, which is not the same as unanswered. The
  // existing screen already tracks it this way, so the two agree.
  isIgnored ( index : number ) : boolean {
    return !!this.rowHidden?.[ index ];
  }

  isMatched ( index : number ) : boolean {
    return !!this.importStructure?.[ index ]?.ours;
  }

  needsDecision ( index : number ) : boolean {
    if ( this.isIgnored(index) ) {
      return false;
    }

    return this.isQuestion(index) || !this.isMatched(index);
  }

  // What the column was matched to, in the words the operator sees everywhere else,
  // falling back to the stored name if we hold no display name for it.
  ourFieldLabel ( index : number ) : string {
    const ours = this.importStructure?.[ index ]?.ours;

    if ( !ours ) {
      return null;
    }

    const match = ( this.importOptions.ourColumns || [] )
      .find(column => column.name === ours);

    return match ? match.display : ours;
  }

  // One row open at a time. Opening a second closes the first, so the list cannot grow
  // back into the wall of controls this replaced.
  toggleRow ( index : number ) : void {
    this.expandedRow = this.expandedRow === index ? null : index;
  }

  buildGrid () : void {
    if ( !this.csvArray?.length ) {
      return;
    }

    this.importStructure         = [];
    this.ourTransformedColumns   = [];
    this.rowHidden               = [];
    this.theirColumns            = [];
    this.theirTransformedColumns = [];

    // Now lets loop through the csv array and manipulate each array inside of it
    for ( let i = 0; i < this.csvArray[ 0 ].length ; i++ ) {
      // If first row contains column names
      if ( this.hasHeaders ) {

        // Each column gets an object in our import structure with empty data except
        // 'theirs'. Their column names don't matter on the backend so just specify the
        // index in the row
        this.importStructure.push({
          action    : 'ignore_column',
          ours      : '',
          theirs    : i,
          user_input: []
        });

        // BUT their column needs to display the name so do the same thing as the
        // import structure but display the name instead of the index
        this.theirTransformedColumns.push({
          name  : this.csvArray[ 0 ][ i ],
          values: []
        });

        // Build a list of column names for the new column dropdown
        this.theirColumns.push({
          index : i,
          name  : this.csvArray[ 0 ][ i ],
          values: []
        });

        let x = 1;

        // No we need to loop through the csv array to get some example data.
        // Get the first four records after the column names or all the records,
        // whichever comes first
        while ( x <= 4 && x < this.csvArray.length ) {
          this.theirTransformedColumns[ i ].values.push(this.csvArray[ x ][ i ]);

          this.theirColumns[ i ].values.push(this.csvArray[ x ][ i ]);

          x++;
        }
      }
      // If first row doesn't contain column names
      else {
        // Same as above, except we create a column row, with fake names to display
        this.importStructure.push({
          theirs    : i,
          ours      : '',
          action    : 'ignore_column',
          user_input: []
        });

        this.theirTransformedColumns.push({
          name  : `Column${ i + 1 }`,
          values: []
        });

        this.theirColumns.push({
          index : i,
          name  : `Column${ i + 1 }`,
          values: []
        });

        let x = 0;

        // This is the same too except we can start at 0 since their are no
        // column names to worry about
        while ( x < 4 && x < this.csvArray.length ) {
          this.theirTransformedColumns[ i ].values.push(this.csvArray[ x ][ i ]);

          this.theirColumns[ i ].values.push(this.csvArray[ x ][ i ]);

          x++;
        }
      }

      this.rowHidden.push(false);
    }
  }

  chooseColumn () : void {
    this.selectColumnVisible = true;
  }

  ignore ( index : number ) : void {
    this.importStructure[ index ].action = 'ignore_column';
    this.rowHidden[ index ]              = true;

    // WS-6782. Setting a column aside IS an answer to its question, so the question
    // goes with it. Choosing a field already drops the question, in moveToStructure,
    // but the ignore route did not, so a question the operator had just dealt with
    // stayed on screen and the count above the list kept counting it.
    this.proposalQuestions = this.proposalQuestions
      .filter(question => question.index !== index);
  }

  // Every column starts as ignore_column, and the guard below only ever fired on a
  // column that was mapped to nothing. So an import in which the operator mapped
  // nothing at all passed straight through: the backend accepted it, ran it, wrote no
  // rows, and reported success. WS-6753 then ticked the type complete, because it asks
  // whether an import record exists rather than whether anything was imported.
  //
  // That is a worse failure than the one this epic exists to remove. A visible failure
  // prompts a retry. A success that imports nothing means a trial customer believes
  // their data is loaded and moves on.
  get hasMappedColumn () : boolean {
    return ( this.importStructure || [] )
      .some(row => row.action !== 'ignore_column' && !!row.ours);
  }

  import () : void {
    this.errors = [];

    for ( let i = 0; i < this.importStructure.length; i++ ) {
      const row = this.importStructure[ i ];

      if ( row.action && row.action !== 'ignore_column' && !row.ours ) {
        this.errors.push(i);
      }
    }

    if ( this.errors.length ) {
      this.messagesService.show(this.translateService.instant('JS_SPACE.IMPORTS.SELECT_OR_SPECIFY_COLUMNS'), 'error');

      return;
    }

    if ( !this.hasMappedColumn ) {
      this.messagesService.show(this.translateService.instant('IMPORT_SPACE.NOTHING_MAPPED'), 'error');

      return;
    }

    this.importsService.start(this.importOptions.url, this.selectedFile, this.importStructure, this.hasHeaders)
    // WS-6789. start() resolves with the run summary, not a string. Handing that object
    // to the message service put it through a text pipe, which threw, which rejected the
    // promise, which showed a RED ERROR on an import that had just succeeded. Precisely
    // the false-failure signal the epic was built to remove, and it makes the operator
    // upload again and write every customer twice.
    .then(( summary : ImportRunSummaryModel ) => {
      const message = summary?.result_message
        || this.translateService.instant('IMPORT_SPACE.IMPORT_FINISHED');

      this.messagesService.show(message, 'success');

      this.runInFlight = summary;

      // The result stays on screen. This only tells the hub to recount, so the
      // heading agrees with what the operator has just been told.
      this.finished.emit();
    })
    .catch(error => this.messagesService.error(error));
  }

  moveToStructure ( index : number, columnName : string ) : void {
    const selected = this.importOptions.ourColumns.find(column => column.name === columnName);

    this.ourTransformedColumns[ index ] = selected;

    this.errors = this.errors.filter(error => error !== index);

    if ( !selected ) {
      return;
    }

    this.importStructure[ index ].ours = selected.name;

    // WS-6751. Where the field determines the correction rule, set it. Choosing
    // Imported ID already says the column is the customer's own identifier, and making
    // the operator then pick "Is Customer ID" is the screen asking them to state the
    // same fact twice. Raised by a customer in September 2025 against customer data and
    // again against vehicle data.
    const implied = this.importsService
      .identifierActionFor(this.importOptions.url, selected.name);

    if ( implied ) {
      this.importStructure[ index ].action     = implied;
      this.importStructure[ index ].user_input = [];

      // The column is answered now, so it stops being a question.
      this.proposalQuestions = this.proposalQuestions
        .filter(question => question.index !== index);

      return;
    }

    this.proposalQuestions = this.proposalQuestions
      .filter(question => question.index !== index);

    if ( this.importStructure[ index ].action === 'ignore_column' ) {
      if ( this.importsService.isPhoneColumn(selected.name) ) {
        this.importStructure[ index ].action = 'extract_numeric';
      }
      else {
        this.importStructure[ index ].action = 'leave_as_is';
      }
    }
  }

  ngOnDestroy () : void {
    this.fileInput.nativeElement.value = '';

    this.importsService.stopWatchingRun();
  }

  // WS-6750. An import outlives the screen that started it, so opening this screen has
  // to ask whether one is already going rather than assume not. Without this, leaving
  // and coming back shows an idle screen next to a running import, and the operator
  // starts a second one.
  ngOnInit () : void {
    // WS-6816. Asks for a run worth returning to, not only one still going. A finished
    // run with rows nobody has dealt with is picked up here, so leaving and coming back
    // lands on the result and its outstanding rows rather than a blank screen.
    //
    // watchRun is still called for it. That is deliberate and costs one request: it
    // resolves immediately for anything that is not queued or running, so a finished run
    // simply settles where it already is rather than needing a separate path.
    this.importsService.runToResume().then(summary => {
      if ( !summary ) {
        return;
      }

      this.runInFlight = summary;

      this.importsService.watchRun(current => {
        this.runInFlight = current;
      })
      .then(finished => {
        this.runInFlight = finished;
      });
    });
  }

  onActionChange ( index : number ) : void {
    this.importStructure[ index ].user_input = [];
  }

  onFileSelect ( event : Event ) : void {
    const input = event.target as HTMLInputElement;

    if ( input.files && input.files.length > 0 ) {
      this.useFile(input.files[ 0 ]);

      if ( !this.selectedFile ) {
        input.value = '';
      }
    }
  }

  // WS-6784. Dropping a file and choosing one are the same act, so both end in
  // useFile rather than each keeping its own idea of what has been picked.
  onDragLeave () : void {
    this.isDragging = false;
  }

  onDragOver ( event : DragEvent ) : void {
    // Both of these are needed. Without them the browser treats the drop as a
    // navigation, leaves the app and opens the file, losing anything already set up.
    event.preventDefault();
    event.stopPropagation();

    this.isDragging = true;
  }

  onDrop ( event : DragEvent ) : void {
    event.preventDefault();
    event.stopPropagation();

    this.isDragging = false;

    const file = event.dataTransfer?.files?.[ 0 ];

    if ( file ) {
      this.useFile(file);
    }
  }

  useFile ( file : File ) : void {
    if ( file ) {
      this.selectedFile = file;
      this.fileName     = this.selectedFile.name;
    }
    else {
      this.selectedFile = null;
      this.fileName     = null;
    }
  }

  selectedEquation ( index : number ) : any {
    const action = this.importStructure[ index ]?.action;

    if ( !action ) {
      return null;
    }

    return this.importOptions.transformFunctions.find(f => f.action === action) || null;
  }

  toggleBox () : void {
    this.importOptions.collapsed = !this.importOptions.collapsed;
  }
}
